import React, { useEffect, useRef, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { ArrowClockwise, ClockHistory, Database } from "react-bootstrap-icons";

import { getAnalyticsFreshness, invalidateFreshnessCache } from "../../utils/Requests";
import type { FreshnessData } from "../../utils/Requests";

type DataFreshnessBannerProps = {
    customerConfig: CustomerConfig;
    isDarkTheme: boolean;
    tableName?: string;
};

const DOMAIN_LABELS: Record<string, string> = {
    events: "Events",
    personalisation: "Personalisation",
    metadata: "Metadata"
};

const DOMAIN_COLORS: Record<string, string> = {
    events: "#7c5cbf",
    personalisation: "#2196f3",
    metadata: "#00897b"
};

function formatUtcTimestamp(utcString: string): string {
    try {
        const date = new Date(utcString);
        return date.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "") + " UTC";
    } catch {
        return utcString;
    }
}

const SPIN_KEYFRAMES = "@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";

const DataFreshnessBanner: React.FC<DataFreshnessBannerProps> = ({ customerConfig, isDarkTheme, tableName }) => {
    const [freshness, setFreshness] = useState<FreshnessData | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const fetchFreshness = (invalidate = false): void => {
        if (invalidate) {
            invalidateFreshnessCache(customerConfig, tableName);
            setIsRefreshing(true);
        }

        getAnalyticsFreshness(customerConfig, tableName)
            .then(data => {
                setFreshness(data);
                setIsRefreshing(false);
            })
            .catch(() => {
                setIsRefreshing(false);
            });
    };

    useEffect(() => {
        let cancelled = false;

        getAnalyticsFreshness(customerConfig, tableName)
            .then(data => {
                if (!cancelled) setFreshness(data);
            })
            .catch(() => { /* silently hide on error */ });

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerConfig, tableName]);

    if (!freshness) {
        return null;
    }

    const buttonBg = isDarkTheme ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
    const buttonBorder = isDarkTheme ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)";
    const buttonText = isDarkTheme ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)";

    const popover = (
        <Popover id="data-freshness-popover" style={{ maxWidth: "340px" }}>
            <Popover.Title as="div" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", fontWeight: 600, fontSize: "13px", padding: "10px 14px 8px" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Database size={13} style={{ color: "#2196f3" }} />
                    Data Freshness
                </span>
                <button
                    type="button"
                    title="Refresh freshness data"
                    onClick={() => fetchFreshness(true)}
                    disabled={isRefreshing}
                    style={{
                        background: "none",
                        border: "none",
                        padding: "2px 4px",
                        cursor: isRefreshing ? "default" : "pointer",
                        color: "#dee2e6",
                        lineHeight: 1,
                        borderRadius: "4px",
                        opacity: isRefreshing ? 0.4 : 1,
                        transition: "opacity 0.2s"
                    }}
                >
                    <ArrowClockwise
                        size={16}
                        strokeWidth={2}
                        style={{
                            display: "block",
                            animation: isRefreshing ? "spin 0.8s linear infinite" : "none"
                        }}
                    />
                </button>
            </Popover.Title>
            <Popover.Content style={{ padding: "0 14px 12px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {freshness.domains.map(domain => {
                        const color = DOMAIN_COLORS[domain.domain] ?? "#6c757d";
                        return (
                            <div
                                key={domain.domain}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                    padding: "7px 10px",
                                    borderRadius: "6px",
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                    border: "1px solid rgba(0,0,0,0.07)"
                                }}
                            >
                                <span style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "12px", color }}>
                                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color, display: "inline-block", flexShrink: 0 }} />
                                    {DOMAIN_LABELS[domain.domain] ?? domain.domain}
                                </span>
                                <span style={{ fontSize: "11px", color: "#6c757d", whiteSpace: "nowrap" }}>
                                    {formatUtcTimestamp(domain.last_updated_utc)}
                                </span>
                            </div>
                        );
                    })}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#9e9e9e", paddingTop: "2px" }}>
                        <ClockHistory size={10} />
                        Collected {formatUtcTimestamp(freshness.collected_at_utc)}
                    </div>
                </div>
            </Popover.Content>
        </Popover>
    );

    return (
        <>
            <style>{SPIN_KEYFRAMES}</style>
            <div ref={containerRef} data-export-ignore>
                <OverlayTrigger trigger="click" rootClose placement="bottom-end" overlay={popover}>
                    <button
                        type="button"
                        title="Data freshness"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: buttonText,
                            backgroundColor: buttonBg,
                            border: `1px solid ${buttonBorder}`,
                            borderRadius: "6px",
                            cursor: "pointer",
                            lineHeight: 1.5,
                            whiteSpace: "nowrap"
                        }}
                    >
                        <Database size={13} />
                        Data freshness
                    </button>
                </OverlayTrigger>
            </div>
        </>
    );
};

export default DataFreshnessBanner;
